import { useEffect, useState, useCallback } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { API } from '../config/api';
import { Card } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { Badge } from '../components/ui/badge';
import { Skeleton } from '../components/ui/skeleton';
import { toast } from 'sonner';
import {
  GraduationCap, Clock, Sparkles, CheckCircle2, ArrowRight, Trophy, Zap, ChevronLeft,
} from 'lucide-react';

function LessonCard({ lesson, completed, onClick }) {
  return (
    <button
      onClick={onClick}
      data-testid={`lesson-${lesson.slug}`}
      className="group text-left p-5 rounded-2xl bg-card/60 backdrop-blur border border-white/10 hover:border-yellow-500/40 hover:-translate-y-0.5 transition-all relative overflow-hidden"
    >
      {completed && (
        <div className="absolute top-3 right-3 flex items-center gap-1 px-2 py-0.5 rounded-full bg-emerald-500/15 text-emerald-400 border border-emerald-500/30 text-[10px] font-black uppercase tracking-widest">
          <CheckCircle2 className="w-3 h-3" /> Done
        </div>
      )}
      <div className="flex items-center gap-2 mb-2 text-[10px] uppercase font-black tracking-widest text-muted-foreground">
        <Badge variant="outline" className="text-[10px] uppercase">{lesson.level}</Badge>
        <span className="flex items-center gap-1"><Clock className="w-3 h-3" />{lesson.duration_min} min</span>
        <span className="flex items-center gap-1 text-amber-400"><Sparkles className="w-3 h-3" />+{lesson.xp} XP</span>
      </div>
      <h3 className="font-black text-base sm:text-lg mb-1 group-hover:text-yellow-400 transition-colors">{lesson.title}</h3>
      <p className="text-sm text-muted-foreground line-clamp-2">{lesson.summary}</p>
      <ArrowRight className="w-4 h-4 mt-3 text-yellow-400 opacity-0 group-hover:opacity-100 transition-opacity" />
    </button>
  );
}

// Simple markdown-to-JSX (headers + bold + paragraphs; no external dep)
function renderBody(body) {
  const lines = body.split('\n');
  const out = [];
  let paragraph = [];
  const flush = (key) => {
    if (paragraph.length) {
      out.push(<p key={`p-${key}`} className="text-sm leading-relaxed mb-3">{formatInline(paragraph.join(' '))}</p>);
      paragraph = [];
    }
  };
  const formatInline = (t) => {
    const parts = [];
    let last = 0;
    const re = /\*\*([^*]+)\*\*/g;
    let m;
    while ((m = re.exec(t)) !== null) {
      if (m.index > last) parts.push(t.slice(last, m.index));
      parts.push(<strong key={parts.length} className="font-black text-foreground">{m[1]}</strong>);
      last = m.index + m[0].length;
    }
    if (last < t.length) parts.push(t.slice(last));
    return parts;
  };
  lines.forEach((line, i) => {
    if (line.startsWith('## ')) {
      flush(i);
      out.push(<h3 key={`h-${i}`} className="text-lg font-black mt-6 mb-2">{line.slice(3)}</h3>);
    } else if (/^\d+\.\s/.test(line)) {
      flush(i);
      out.push(<div key={`li-${i}`} className="text-sm leading-relaxed mb-1 ml-1">{formatInline(line)}</div>);
    } else if (line.startsWith('- ')) {
      flush(i);
      out.push(<div key={`b-${i}`} className="text-sm leading-relaxed mb-1 ml-1">• {formatInline(line.slice(2))}</div>);
    } else if (line.trim() === '') {
      flush(i);
    } else {
      paragraph.push(line);
    }
  });
  flush('final');
  return out;
}

function LessonDetail({ slug, onBack, onComplete }) {
  const { getAuthHeaders } = useAuth();
  const [lesson, setLesson] = useState(null);
  const [loading, setLoading] = useState(true);
  const [completed, setCompleted] = useState(false);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      setLoading(true);
      try {
        const [l, p] = await Promise.all([
          fetch(`${API}/academy/lessons/${slug}`).then(r => r.json()),
          fetch(`${API}/academy/progress`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { completed_slugs: [] }),
        ]);
        setLesson(l);
        setCompleted(p.completed_slugs?.includes(slug));
      } catch { toast.error('Could not load lesson'); }
      setLoading(false);
    })();
  }, [slug, getAuthHeaders]);

  const markDone = async () => {
    if (completed) return;
    setSaving(true);
    try {
      const res = await fetch(`${API}/academy/lessons/${slug}/complete`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...getAuthHeaders() },
        body: JSON.stringify({ seconds_read: 60 }),
      });
      if (!res.ok) throw new Error();
      const data = await res.json();
      setCompleted(true);
      toast.success(`+${data.xp_awarded} XP earned!`);
      onComplete?.();
    } catch { toast.error('Could not save'); }
    setSaving(false);
  };

  if (loading || !lesson) return <Skeleton className="h-96 rounded-2xl" />;

  return (
    <div data-testid={`lesson-detail-${slug}`}>
      <Button variant="ghost" onClick={onBack} className="mb-4" data-testid="lesson-back">
        <ChevronLeft className="w-4 h-4 mr-1" /> Back to lessons
      </Button>
      <div className="mb-4 flex items-center gap-2 flex-wrap">
        <Badge variant="outline">{lesson.level}</Badge>
        <span className="flex items-center gap-1 text-xs text-muted-foreground">
          <Clock className="w-3 h-3" />{lesson.duration_min} min
        </span>
        <span className="flex items-center gap-1 text-xs text-amber-400 font-bold">
          <Sparkles className="w-3 h-3" />+{lesson.xp} XP
        </span>
      </div>
      <h1 className="text-3xl sm:text-4xl font-black tracking-tight mb-2">{lesson.title}</h1>
      <p className="text-muted-foreground mb-8">{lesson.summary}</p>
      <div className="prose prose-invert max-w-none">{renderBody(lesson.body)}</div>
      <div className="mt-8 pt-6 border-t border-white/10">
        {completed ? (
          <div className="p-4 rounded-xl bg-emerald-500/10 border border-emerald-500/25 text-center" data-testid="lesson-completed">
            <CheckCircle2 className="w-6 h-6 text-emerald-400 mx-auto mb-1" />
            <p className="text-sm font-bold text-emerald-400">You&apos;ve completed this lesson</p>
          </div>
        ) : (
          <Button
            onClick={markDone}
            disabled={saving}
            size="lg"
            className="w-full bg-gradient-to-r from-yellow-500 to-orange-500 hover:from-yellow-600 hover:to-orange-600 text-white"
            data-testid="lesson-complete-btn"
          >
            {saving ? 'Saving…' : <><Sparkles className="w-4 h-4 mr-1" /> Mark as complete · +{lesson.xp} XP</>}
          </Button>
        )}
      </div>
    </div>
  );
}

export default function CreatorAcademyPage() {
  const { getAuthHeaders, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const { slug } = useParams();
  const [tracks, setTracks] = useState([]);
  const [lessons, setLessons] = useState([]);
  const [progress, setProgress] = useState({ completed_slugs: [], total_xp: 0 });
  const [activeTrack, setActiveTrack] = useState(null);
  const [loading, setLoading] = useState(true);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, l, p] = await Promise.all([
        fetch(`${API}/academy/tracks`).then(r => r.json()),
        fetch(`${API}/academy/lessons`).then(r => r.json()),
        isAuthenticated
          ? fetch(`${API}/academy/progress`, { headers: getAuthHeaders() }).then(r => r.ok ? r.json() : { completed_slugs: [], total_xp: 0 })
          : Promise.resolve({ completed_slugs: [], total_xp: 0 }),
      ]);
      setTracks(t.tracks || []);
      setLessons(l.lessons || []);
      setProgress(p);
    } catch { toast.error('Could not load academy'); }
    setLoading(false);
  }, [getAuthHeaders, isAuthenticated]);

  useEffect(() => { load(); }, [load]);

  if (slug) {
    return (
      <div className="min-h-screen bg-background">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 py-8">
          <LessonDetail slug={slug} onBack={() => navigate('/academy')} onComplete={load} />
        </div>
      </div>
    );
  }

  const filtered = activeTrack ? lessons.filter(l => l.track === activeTrack) : lessons;

  return (
    <div className="min-h-screen bg-background" data-testid="academy-page">
      {/* Hero */}
      <div className="relative overflow-hidden border-b border-border">
        <div className="absolute inset-0 bg-gradient-to-br from-yellow-500/15 via-orange-500/10 to-red-500/10" />
        <div className="relative max-w-6xl mx-auto px-4 sm:px-6 py-10">
          <div className="flex items-start justify-between gap-4 flex-wrap">
            <div className="flex items-center gap-3">
              <div className="w-14 h-14 rounded-2xl bg-gradient-to-br from-yellow-500 to-orange-500 flex items-center justify-center shadow-xl">
                <GraduationCap className="w-7 h-7 text-white" />
              </div>
              <div>
                <h1 className="text-3xl sm:text-4xl font-black tracking-tight">Creator Academy</h1>
                <p className="text-sm text-muted-foreground">Level up your career with free lessons from the DMV team.</p>
              </div>
            </div>
            <div className="flex items-center gap-2 px-4 py-2 rounded-xl bg-card/60 border border-white/10">
              <Trophy className="w-4 h-4 text-amber-400" />
              <div>
                <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Your XP</p>
                <p className="text-lg font-black" data-testid="academy-xp">{progress.total_xp || 0}</p>
              </div>
              <div className="w-px h-8 bg-white/10 mx-2" />
              <div>
                <p className="text-[10px] uppercase font-black tracking-widest text-muted-foreground">Done</p>
                <p className="text-lg font-black">{progress.completed_slugs?.length || 0} / {lessons.length}</p>
              </div>
            </div>
          </div>
        </div>
      </div>

      <div className="max-w-6xl mx-auto px-4 sm:px-6 py-8">
        {/* Track filter */}
        <div className="flex gap-2 overflow-x-auto pb-3 mb-6 -mx-4 px-4">
          <button
            onClick={() => setActiveTrack(null)}
            data-testid="track-all"
            className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
              !activeTrack ? 'bg-yellow-500 text-white border-yellow-500' : 'border-border hover:border-yellow-500/40'
            }`}
          >
            All lessons
          </button>
          {tracks.map(t => (
            <button
              key={t.slug}
              onClick={() => setActiveTrack(t.slug)}
              data-testid={`track-${t.slug}`}
              className={`shrink-0 px-4 py-2 rounded-full text-sm font-semibold border transition-all ${
                activeTrack === t.slug ? `bg-gradient-to-r ${t.tint} text-white border-transparent` : 'border-border hover:border-yellow-500/40'
              }`}
            >
              {t.label}
              <span className="ml-2 text-[10px] opacity-60">{t.lesson_count}</span>
            </button>
          ))}
        </div>

        {loading ? (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
            {[...Array(6)].map((_, i) => <Skeleton key={i} className="h-40 rounded-2xl" />)}
          </div>
        ) : filtered.length === 0 ? (
          <Card className="p-10 text-center text-muted-foreground">No lessons in this track yet.</Card>
        ) : (
          <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="lessons-grid">
            {filtered.map(l => (
              <LessonCard
                key={l.slug}
                lesson={l}
                completed={progress.completed_slugs?.includes(l.slug)}
                onClick={() => navigate(`/academy/${l.slug}`)}
              />
            ))}
          </div>
        )}
      </div>
    </div>
  );
}
